import React, { useEffect, useState } from "react";
import { SimpleClient } from "../../interfaces/client";
import {
  getClientsBySearch,
  HandleMergeClients,
  softDeleteClient,
} from "../../services/clientService";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";

const MergeClients = () => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [searchTermA, setSearchTermA] = useState("");
  const [searchTermB, setSearchTermB] = useState("");
  const [clientAOptions, setClientAOptions] = useState<SimpleClient[]>([]);
  const [clientBOptions, setClientBOptions] = useState<SimpleClient[]>([]);
  const [selectedClientA, setSelectedClientA] = useState<SimpleClient | null>(
    null,
  );
  const [selectedClientB, setSelectedClientB] = useState<SimpleClient | null>(
    null,
  );
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Debounce search term for Client A
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTermA.trim()) {
        fetchClients(searchTermA.trim(), "A");
      } else {
        setClientAOptions([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTermA]);

  // Debounce search term for Client B
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTermB.trim()) {
        fetchClients(searchTermB.trim(), "B");
      } else {
        setClientBOptions([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTermB]);

  const fetchClients = async (search: string, type: "A" | "B") => {
    try {
      const clients = await getClientsBySearch(search);
      if (type === "A") {
        setClientAOptions(clients);
      } else {
        setClientBOptions(clients);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
    }
  };

  const handleMerge = async () => {
    try {
      setShowConfirmation(false);
      setLoadingSpinner(true);
      if (!selectedClientA || !selectedClientB) return;

      const message = await HandleMergeClients(
        selectedClientA?.clientID,
        selectedClientB?.clientID,
      );
      if (message) {
        setSuccessMessage(message.message);
        setSelectedClientB(null);
        setSearchTermB("");
        setClientBOptions([]);

        await softDeleteClient(selectedClientB.clientID);
      } else {
        setErrorMessage("Failed to merge clients");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          <h1>Merge Clients</h1>
        </div>
      </div>
      <div className="divider"></div>
      <div>
        Merge two clients together
        <ul>
          <li>Pick a Client A and a Client B</li>
          <li>
            Client B's <b>orders</b> and <b>general documents</b> will merge
            into client A's
          </li>
          <li>
            Client B will be deleted
            <ul>
              <li className="error">
                <b>
                  Transfer all needed client information over to Client A prior
                  to merging-print client screen for assistance
                </b>
              </li>
              <li>
                ex: contacts, notes, questionnaire wording, profile information
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div className="divider-small"></div>
      <br></br>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className="data-container" style={{ width: "48%" }}>
          <div className="space-between-container" style={{ gap: "10px" }}>
            <div className="text-sub-header">Client A</div>
            <div
              className="search-container"
              style={{ width: "400px", position: "relative" }}
            >
              <input
                style={{ border: "none", width: "100%", outline: "none" }}
                type="text"
                placeholder="Choose Client A..."
                value={searchTermA}
                onChange={(e) => {
                  setSearchTermA(e.target.value);
                  setSelectedClientA(null);
                }}
              />
              {searchTermA.trim() && (
                <i
                  className="bi bi-x-circle clear-icon"
                  onClick={() => setSearchTermA("")}
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                ></i>
              )}

              {clientAOptions.length > 0 && !selectedClientA && (
                <ul className="dropdown-list">
                  {clientAOptions.map((client) => (
                    <li
                      key={client.clientID}
                      onClick={() => {
                        setSelectedClientA(client);
                        setSearchTermA(client.clientName);
                        setClientAOptions([]);
                      }}
                    >
                      {client.clientName} ({client.diocese})
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label className="field-label">Client Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientA?.clientName}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Client Name Merge Field:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientA?.diocese}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Denomination:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientA?.denomination}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Primary First Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientA?.primaryFirstName}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Primary Last Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientA?.primaryLastName}
                  disabled
                />
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <button
              className={`smbtn btn-grey ${
                selectedClientA === null ? "disabled" : ""
              }`}
              onClick={() =>
                (window.location.href = `/admin/view-client/${selectedClientA?.clientID}`)
              }
              disabled={selectedClientA === null}
            >
              View Client
            </button>
          </div>
        </div>
        <div className="arrow-icon">
          <i className="bi bi-arrow-left"></i>
        </div>
        <div className="data-container" style={{ width: "48%" }}>
          <div className="space-between-container">
            <div className="text-sub-header">Client B</div>
            <div
              className="search-container"
              style={{ width: "400px", position: "relative" }}
            >
              <input
                style={{ border: "none", width: "100%", outline: "none" }}
                type="text"
                placeholder="Choose Client B..."
                value={searchTermB}
                onChange={(e) => {
                  setSearchTermB(e.target.value);
                  setSelectedClientB(null);
                }}
              />
              {searchTermB.trim() && (
                <i
                  className="bi bi-x-circle clear-icon"
                  onClick={() => setSearchTermB("")}
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                ></i>
              )}

              {clientBOptions.length > 0 && !selectedClientB && (
                <ul className="dropdown-list">
                  {clientBOptions.map((client) => (
                    <li
                      key={client.clientID}
                      onClick={() => {
                        setSelectedClientB(client);
                        setSearchTermB(client.clientName);
                        setClientBOptions([]);
                      }}
                    >
                      {client.clientName} ({client.diocese})
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label className="field-label">Client Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientB?.clientName}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Client Name Merge Field:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientB?.diocese}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Denomination:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientB?.denomination}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Primary First Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientB?.primaryFirstName}
                  disabled
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Primary Last Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={selectedClientB?.primaryLastName}
                  disabled
                />
              </div>
            </div>
            <div className="action-buttons">
              <button
                className={`smbtn btn-grey ${
                  selectedClientB === null ? "disabled" : ""
                }`}
                onClick={() =>
                  (window.location.href = `/admin/view-client/${selectedClientA?.clientID}`)
                }
                disabled={selectedClientB === null}
              >
                View Client
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="btn-center">
        <button
          className={`oxbtn btn-green ${
            selectedClientA === null || selectedClientB === null
              ? "disabled"
              : ""
          }`}
          onClick={() => setShowConfirmation(true)}
          disabled={selectedClientA === null || selectedClientB === null}
        >
          Merge
        </button>
      </div>
      {showConfirmation && (
        <ConfirmPopup
          onConfirm={() => handleMerge()}
          onCancel={() => setShowConfirmation(false)}
          message="Are you sure you would like to merge these two clients? There is no un-doing this action."
        />
      )}

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default MergeClients;
